/**
 * Post-finish parking autopilot (human or bot, while others still race).
 *
 * - With a map `parkArea`: hurry there, then ease and stop inside the ring.
 * - Without one: reverse ~180° from the finish heading and stop.
 *
 * Aim chrome (movement / prediction / move line) stays hidden in GameScene; the rules still
 * use the movement disk.
 */
import { candidateDestinations } from './bot/candidates.ts';
import { isInsideParkArea, parkAreaCentroid } from './map/parkArea.ts';
import type { MapDocument } from './map/types.ts';
import { add, angleDelta, fromAngle, length, normalize, scale, sub } from './math.ts';
import { solveTurnArc } from './movement.ts';
import type { FleetContext } from './turn.ts';
import { planWithFeel, type TurnFeel } from './turnFeel.ts';
import type { BoatState, Vec2 } from './types.ts';

/** Rest below this kept speed (px/turn) counts as stopped. */
export const PARK_SPEED_EPS = 2;
/** Soft arrival speed inside / at the park ring. */
export const PARK_ARRIVE_SPEED = 40;
/** Start easing once this many clearances from the park target. */
const BRAKE_RANGE_CLEARANCES = 2.5;
/** Heading must be this close to the reverse aim (radians) to finish a no-area park. */
export const PARK_REVERSE_HEADING_EPS = (18 * Math.PI) / 180;
/** Sample density for autopilot clicks. */
const PARK_DENSITY = 72;
/** Hard stop after this many park turns so a trapped hull cannot stall the match. */
export const PARK_TURN_CAP = 48;

export type ParkContext = {
  map: MapDocument;
  feel: TurnFeel;
  fleet?: FleetContext;
  parkTurns?: number;
  /**
   * Finish heading + π. Used when the map has no park area.
   * Omitted → reverse of the boat's current heading.
   */
  reverseHeading?: number;
};

export function parkTargetSpeed(
  rangeToTarget: number,
  clearancePx: number,
  cruisePx: number,
): number {
  const brakeFrom = Math.max(clearancePx, 1) * BRAKE_RANGE_CLEARANCES;
  const cruise = Math.max(cruisePx, PARK_ARRIVE_SPEED);

  if (rangeToTarget >= brakeFrom) {
    return cruise;
  }

  const t = rangeToTarget / brakeFrom;
  return PARK_ARRIVE_SPEED + (cruise - PARK_ARRIVE_SPEED) * t;
}

export function reverseParkHeading(finishHeading: number): number {
  return angleDelta(0, finishHeading + Math.PI);
}

/** True when the hull has stopped in the park ring, or completed a reverse-and-stop. */
export function isParkSettled(
  boat: BoatState,
  map: MapDocument,
  reverseHeading: number | null,
): boolean {
  if (boat.speed > PARK_SPEED_EPS) {
    return false;
  }

  if (map.parkArea) {
    return isInsideParkArea(map, boat.position);
  }

  if (reverseHeading === null) {
    return true;
  }

  return Math.abs(angleDelta(boat.heading, reverseHeading)) <= PARK_REVERSE_HEADING_EPS;
}

export function shouldMarkParked(
  boat: BoatState,
  map: MapDocument,
  parkTurns: number,
  reverseHeading: number | null,
): boolean {
  if (parkTurns >= PARK_TURN_CAP) {
    return true;
  }

  return isParkSettled(boat, map, reverseHeading);
}

/**
 * Autopilot click: race to the park area (or reverse heading) while far; ease and stop
 * for the landing. Prefers a right turn when both sides work.
 */
export function decideParkDestination(boat: BoatState, ctx: ParkContext): Vec2 {
  const { map, feel, fleet } = ctx;
  const parkTurns = ctx.parkTurns ?? 0;
  const reverse =
    ctx.reverseHeading ?? reverseParkHeading(boat.heading);
  const target = parkAreaCentroid(map);
  const rangeNow = target ? length(sub(target, boat.position)) : 0;
  const cruise = Math.max(boat.speed, feel.radius);
  const inside = map.parkArea ? isInsideParkArea(map, boat.position) : false;
  const targetSpeed =
    target && !inside
      ? parkTargetSpeed(rangeNow, feel.radius, cruise)
      : PARK_ARRIVE_SPEED;
  const candidates = candidateDestinations(boat, map, feel, PARK_DENSITY);
  let best: { dest: Vec2; score: number } | null = null;

  for (const dest of candidates) {
    const planned = planWithFeel(boat, dest, map, feel, fleet);

    if (!planned) {
      continue;
    }

    const landed = planned.boat;
    const arc = solveTurnArc(boat, planned.clipped ? landed.position : dest, feel.turnTax);
    const offset = arc?.offset ?? 0;
    const leftPenalty = offset < 0 ? -offset * 40 : 0;
    const rightBonus = offset > 0 ? offset * 12 : 0;
    const speedErr = Math.abs(landed.speed - targetSpeed);
    let score = speedErr * (inside || !target ? 0.06 : 0.02) + leftPenalty - rightBonus;
    score += planned.clipped ? 40 : 0;

    if (target) {
      const rangeAfter = length(sub(target, landed.position));
      const opened = Math.max(0, rangeAfter - rangeNow);
      const closed = Math.max(0, rangeNow - rangeAfter);
      const near = rangeNow < feel.radius * BRAKE_RANGE_CLEARANCES || inside;
      score += rangeAfter * (near ? 0.025 : 0.055);
      score += opened * 0.1;
      score -= closed * (near ? 0.02 : 0.07);
      if (inside || isInsideParkArea(map, landed.position)) {
        score += landed.speed * 0.08;
      }
    } else {
      const headingErrNow = Math.abs(angleDelta(boat.heading, reverse));
      const headingErrAfter = Math.abs(angleDelta(landed.heading, reverse));
      score += headingErrAfter * 50;
      score += Math.max(0, headingErrAfter - headingErrNow) * 40;
      score += landed.speed * 0.04;
    }

    if (!best || score < best.score) {
      best = { dest, score };
    }
  }

  if (best) {
    return best.dest;
  }

  if (target && parkTurns < PARK_TURN_CAP) {
    const step = Math.min(
      feel.radius * (rangeNow > feel.radius * BRAKE_RANGE_CLEARANCES && !inside ? 0.98 : 0.3),
      Math.max(rangeNow, 1),
    );
    const aimed = add(boat.position, scale(normalize(sub(target, boat.position)), step));
    if (planWithFeel(boat, aimed, map, feel, fleet)) {
      return aimed;
    }
  }

  if (!target) {
    const turn = fromAngle(boat.heading + Math.PI / 2, feel.radius * 0.7);
    const aimed = add(boat.position, turn);
    if (planWithFeel(boat, aimed, map, feel, fleet)) {
      return aimed;
    }
  }

  return add(boat.position, fromAngle(boat.heading, Math.max(feel.radius * 0.15, 1)));
}
